import { useRef, useState } from 'react';

import ReactDOM from 'react-dom';
import classNames from 'classnames';

import useEffectAfterMount from 'hooks/useEffectAfterMount';
import useWindowSize from 'hooks/useWindowSize';

import helper, {
  Box,
  HorizontalAnchorsAdjusted,
  PositionHorizontal,
  PositionVertical,
  VerticalAnchorsAdjusted,
} from './helper';

import './PopoverPanel.scoped.scss';

export type PositionString =
  | 'top.left'
  | 'top.center'
  | 'top.right'
  | 'center.left'
  | 'center.center'
  | 'center.right'
  | 'bottom.left'
  | 'bottom.center'
  | 'bottom.right';

interface Props {
  children?: React.ReactNode;
  targetRef: React.MutableRefObject<any>;
  targetElementPos?: PositionString;
  childrenElementPos?: PositionString;
  childrenAtleastFullWidth?: boolean;
  childrenContainerClassName?: string;
  onBackdropClick?: () => void;
  paddingVertical?: number;
  paddingHorizontal?: number;
}

const modalElement = document.getElementById('popover');

const PopoverPanel: React.FC<Props> = props => {
  const {
    children,
    targetRef,
    targetElementPos = 'bottom.left',
    childrenElementPos = 'top.left',
    onBackdropClick = () => null,
    childrenAtleastFullWidth,
    childrenContainerClassName,
    paddingVertical = 0,
    paddingHorizontal = 0,
  } = props;

  const targetElementPosVertical = targetElementPos.split('.')[0] as PositionVertical;
  const targetElementPosHorizontal = targetElementPos.split('.')[1] as PositionHorizontal;

  const childrenElementPosVertical = childrenElementPos.split('.')[0] as PositionVertical;
  const childrenElementPosHorizontal = childrenElementPos.split('.')[1] as PositionHorizontal;

  const [isMeasurmentRender, setIsMeasurmentRender] = useState(true);
  const [targetRect, setTargetRect] = useState<Box>(null);
  const [verticalAnchorsAdjusted, setVerticalAnchorsAdjusted] =
    useState<VerticalAnchorsAdjusted>(null);
  const [horizontalAnchorsAdjusted, setHorizontalAnchorsAdjusted] =
    useState<HorizontalAnchorsAdjusted>(null);

  const childrenRef = useRef(null);

  const setPosition = () => {
    const childrenBox: DOMRect = childrenRef.current.getBoundingClientRect();
    const targetBoxRect: DOMRect = targetRef.current.getBoundingClientRect();

    const targetBox = helper.addPadding(targetBoxRect, paddingVertical, paddingHorizontal);

    setIsMeasurmentRender(false);
    setTargetRect(targetBox);

    const verticalAnchorPointsAdjusted = helper.getVerticalAnchorPointsAdjusted(
      targetBox,
      childrenBox,
      targetElementPosVertical,
      childrenElementPosVertical
    );
    const horizontalAnchorPointsAdjusted = helper.getHorizontalAnchorPointsAdjusted(
      targetBox,
      childrenBox,
      targetElementPosHorizontal,
      childrenElementPosHorizontal
    );

    setVerticalAnchorsAdjusted(verticalAnchorPointsAdjusted);
    setHorizontalAnchorsAdjusted(horizontalAnchorPointsAdjusted);
  };

  const windowSize = useWindowSize();

  useEffectAfterMount(() => {
    setIsMeasurmentRender(true);
  }, [windowSize]);

  const setRef = node => {
    childrenRef.current = node;

    if (node && isMeasurmentRender) {
      setPosition();
      if (node.firstChild) {
        node.firstChild.focus();
      }
    }
  };

  const styles = helper.getCssStylesByAnchors(
    verticalAnchorsAdjusted,
    horizontalAnchorsAdjusted,
    isMeasurmentRender,
    targetRect,
    childrenAtleastFullWidth
  );

  return ReactDOM.createPortal(
    <div>
      <div className="backdrop" onClick={() => onBackdropClick()}></div>
      <div
        tabIndex={1}
        className={classNames(['children-container', childrenContainerClassName])}
        ref={setRef}
        style={styles}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    modalElement
  );
};

export default PopoverPanel;
